import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { http, setToken, getToken } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const LEVELS = { none: 0, view: 1, manage: 2 };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = anon
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = getToken();
    if (!t) { setUser(null); setReady(true); return; }
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null)).finally(() => setReady(true));
  }, []);

  // Step 1: credentials + captcha. Returns {mfa_required|mfa_setup_required, mfa_token, email}
  const login = useCallback(async (email, password, captcha) => {
    const { data } = await http.post("/auth/login", {
      email, password, captcha_id: captcha?.captcha_id, captcha_answer: captcha?.answer });
    return data;
  }, []);
  // Step 2a: begin TOTP enrolment
  const mfaSetup = useCallback((mfaToken) => http.post("/auth/mfa/setup", { mfa_token: mfaToken }).then((r) => r.data), []);
  // Step 2b: confirm enrolment with first code -> logs in
  const mfaEnable = useCallback(async (mfaToken, code) => {
    const { data } = await http.post("/auth/mfa/enable", { mfa_token: mfaToken, code });
    setToken(data.token); setUser(data.user);
    return data; // { token, user, backup_codes }
  }, []);
  // Step 2c: verify code for already-enrolled user -> logs in
  const mfaVerify = useCallback(async (mfaToken, code) => {
    const { data } = await http.post("/auth/mfa/verify", { mfa_token: mfaToken, code });
    setToken(data.token); setUser(data.user);
    return data.user;
  }, []);

  const refreshUser = useCallback(() => http.get("/auth/me").then((r) => setUser(r.data)).catch(() => {}), []);
  const logout = useCallback(() => { setToken(null); setUser(null); window.location.href = "/login"; }, []);

  const can = useCallback((cap, level = "view") => {
    if (!user) return false;
    if (user.role === "super_admin") return true;
    const eff = user.permissions_effective || {};
    return (LEVELS[eff[cap] || "none"] || 0) >= (LEVELS[level] || 0);
  }, [user]);
  const isAdmin = useCallback(() => user && (user.role === "super_admin" || can("admin.users", "view") || can("admin.orgs", "view")), [user, can]);

  const value = useMemo(
    () => ({ user, ready, login, mfaSetup, mfaEnable, mfaVerify, refreshUser, logout, can, isAdmin }),
    [user, ready, login, mfaSetup, mfaEnable, mfaVerify, refreshUser, logout, can, isAdmin]);

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}
